// ContactForm.tsx - VERSIÓN CORREGIDA Y RESPONSIVA
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  PaperAirplaneIcon, 
  CheckCircleIcon, 
  ExclamationCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  InformationCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface ContactFormProps {
  onClose?: () => void;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
}

const ContactForm: React.FC<ContactFormProps> = ({ onClose }) => {
  // ==================== ESTADOS ====================
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // ==================== CONFIGURACIÓN API ====================
  const getApiBaseUrl = () => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
      
      if (isLocalhost) {
        return 'http://localhost:3000/api';
      } else if (hostname.includes('vercel.app')) {
        return `https://${hostname}/api`;
      } else {
        return '/api';
      }
    }
    return '/api';
  };

  const API_BASE_URL = getApiBaseUrl();
  const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';
  
  // ==================== VALIDACIÓN ====================
  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'El nombre es requerido';
        if (value.length < 2) return 'El nombre debe tener al menos 2 caracteres';
        if (value.length > 100) return 'El nombre es demasiado largo';
        break;
      case 'email':
        if (!value.trim()) return 'El email es requerido';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Email inválido';
        if (value.length > 255) return 'El email es demasiado largo';
        break;
      case 'phone':
        if (value && !/^[\d\s+\-()]{6,30}$/.test(value)) return 'Teléfono inválido (6-30 caracteres)';
        break;
      case 'company':
        if (value && value.length > 100) return 'El nombre de empresa es demasiado largo';
        break;
      case 'message':
        if (!value.trim()) return 'El mensaje es requerido';
        if (value.length < 10) return 'El mensaje debe tener al menos 10 caracteres';
        if (value.length > 2000) return 'El mensaje es demasiado largo (máximo 2000 caracteres)';
        break;
    }
    return undefined;
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    
    const error = validateField(name, formData[name as keyof typeof formData]);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    let processedValue = value;
    if (name === 'name' && value.length > 100) return;
    if (name === 'email' && value.length > 255) return;
    if (name === 'phone' && value.length > 30) return;
    if (name === 'company' && value.length > 100) return;
    if (name === 'message' && value.length > 2000) return;
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    if (touched[name]) {
      const error = validateField(name, processedValue);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
    
    if (error) setError(null);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    Object.keys(formData).forEach(key => {
      if (key === 'phone' || key === 'company') return;
      
      const error = validateField(key, formData[key as keyof typeof formData]);
      if (error) {
        newErrors[key as keyof FormErrors] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    
    const allTouched = Object.keys(formData).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {} as Record<string, boolean>);
    setTouched(allTouched);

    return isValid;
  };

  // ==================== ENVÍO CON REINTENTOS ====================
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      console.log('❌ Validación fallida', errors);
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    setRetryCount(0);
    
    // Función para reintentos
    const submitWithRetry = async (attempt: number = 0): Promise<boolean> => {
      const maxRetries = 2;
      
      try {
        console.log(`🚀 Intento ${attempt + 1} de ${maxRetries + 1}`);
        console.log('📊 Datos:', formData);
        console.log('🌐 Base URL:', API_BASE_URL);
        
        const apiUrl = `${API_BASE_URL}/send-contact-form`;
        console.log('🎯 Endpoint:', apiUrl);
        
        const payload = {
          ...formData,
          source: 'website_contact_form',
          environment: IS_DEVELOPMENT ? 'development' : 'production',
          pageUrl: typeof window !== 'undefined' ? window.location.href : '',
          timestamp: new Date().toISOString()
        };
        
        console.log('📦 Payload:', payload);
        
        // Configurar timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
          mode: 'cors',
          credentials: 'same-origin'
        });
        
        clearTimeout(timeoutId);
        
        console.log('📡 Respuesta - Status:', response.status);
        
        // Verificar si es JSON
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const text = await response.text();
          console.error('❌ Respuesta no es JSON:', text.substring(0, 200));
          throw new Error(`Formato incorrecto del servidor: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('✅ Respuesta JSON:', data);
        
        if (!response.ok) {
          console.error('❌ Error HTTP:', response.status, data);
          throw new Error(data.message || `Error del servidor: ${response.status}`);
        }
        
        if (!data.success) {
          console.error('❌ Error lógico:', data);
          throw new Error(data.message || 'Error procesando la solicitud');
        }
        
        // ÉXITO
        console.log('🎉 Formulario enviado:', data);
        
        setRequestId(data.data?.requestId || null);
        setIsSubmitted(true);
        
        // Reiniciar después de 5 segundos
        setTimeout(() => {
          console.log('🔄 Reiniciando formulario...');
          setFormData({
            name: '',
            email: '',
            phone: '',
            company: '',
            message: ''
          });
          if (onClose) {
            console.log('🚪 Cerrando formulario...');
            onClose();
          }
        }, 5000);
        
        return true;
        
      } catch (err: any) {
        console.error(`❌ Error en intento ${attempt + 1}:`, err);
        
        // Si hay intentos disponibles, reintentar
        if (attempt < maxRetries) {
          setRetryCount(attempt + 1);
          console.log(`🔄 Reintentando en 2 segundos (${attempt + 1}/${maxRetries})...`);
          
          await new Promise(resolve => setTimeout(resolve, 2000));
          return await submitWithRetry(attempt + 1);
        }
        
        // Sin más intentos, mostrar error
        let errorMessage = '';
        let showTechnicalDetails = IS_DEVELOPMENT;
        
        if (err.name === 'AbortError') {
          errorMessage = 'El servidor tardó demasiado en responder. ';
          showTechnicalDetails = true;
        } else if (err.message && err.message.includes('Failed to fetch')) {
          errorMessage = 'No se pudo conectar al servidor. Verifica tu conexión a internet. ';
          showTechnicalDetails = true;
        } else if (err.message && err.message.includes('CORS')) {
          errorMessage = 'Error de configuración de seguridad (CORS). ';
          showTechnicalDetails = true;
        } else if (err.message && err.message.includes('405')) {
          errorMessage = 'Método no permitido en el servidor. ';
          showTechnicalDetails = true;
        } else {
          errorMessage = 'Error al enviar el formulario. ';
        }
        
        const fullMessage = `
${errorMessage}

Por favor:
1. Verifica tu conexión a internet
2. Intenta nuevamente en unos minutos
3. Si el problema persiste, contáctanos directamente

${showTechnicalDetails ? `Detalles: ${err.message || 'Error desconocido'}` : ''}

Email: contacto@algorithmia.com.ar
Teléfono: +54 (351) 2489015
        `.trim();
        
        setError(fullMessage);
        return false;
      }
    };
    
    // Iniciar el proceso con reintentos
    await submitWithRetry();
    setIsSubmitting(false);
  };

  // ==================== RENDERIZADO: ESTADO ENVIADO ====================
  if (isSubmitted) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-2xl mx-auto bg-gradient-to-br from-[#0E2544] via-[#1c3558] to-[#2D4A7D] rounded-2xl border border-white/20 p-4 sm:p-6 md:p-8"
      >
        <div className="flex flex-col items-center justify-center min-h-[250px] sm:min-h-[300px] text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: "spring" }}
            className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-[#4ADE80] via-[#3BC571] to-[#2D4A7D] rounded-full flex items-center justify-center mb-4"
          >
            <CheckCircleIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
          </motion.div>
          
          <motion.h3
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-2"
          >
            ¡Mensaje Enviado!
          </motion.h3>
          
          <motion.p
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-white/80 text-xs sm:text-sm max-w-md mx-auto mb-4 px-4"
          >
            Hemos recibido tu mensaje correctamente. Te contactaremos en breve.
          </motion.p>
          
          {requestId && (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 text-white/80 rounded-full text-xs"
            >
              <InformationCircleIcon className="h-3 w-3" />
              <span>ID: <span className="font-mono">{requestId}</span></span>
            </motion.div>
          )}
          
          <motion.div
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="bg-white/5 rounded-lg p-3 sm:p-4 border border-white/10 mb-4 w-full max-w-xs sm:max-w-sm"
          >
            <h4 className="font-bold text-white text-xs sm:text-sm mb-2">📧 Confirmación enviada a:</h4>
            <p className="text-white/70 text-xs sm:text-sm break-all">{formData.email}</p>
          </motion.div>
          
          <motion.p
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-white/60 text-xs"
          >
            Esta ventana se cerrará automáticamente...
          </motion.p>
        </div>
      </motion.div>
    );
  }

  // ==================== RENDERIZADO: FORMULARIO ====================
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-2xl mx-auto bg-gradient-to-br from-[#0E2544] via-[#1c3558] to-[#2D4A7D] rounded-2xl border border-white/20 p-3 sm:p-4 md:p-6"
    >
      {/* Encabezado */}
      <div className="mb-4 sm:mb-6 text-center">
        <h3 className="text-base sm:text-lg md:text-xl font-bold text-white mb-1">
          Contáctanos
        </h3>
        <p className="text-white/70 text-xs sm:text-sm">
          Completa el formulario y te responderemos pronto
        </p>
      </div>
      
      {/* Mostrar error general */}
      {error && (
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="mb-3 sm:mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg"
        >
          <div className="flex items-start gap-2">
            <ExclamationCircleIcon className="h-4 w-4 text-red-300 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-red-300 text-xs sm:text-sm whitespace-pre-line">{error}</p>
              {retryCount > 0 && (
                <div className="mt-2 flex items-center gap-2">
                  <ArrowPathIcon className="h-3 w-3 text-yellow-300 animate-spin" />
                  <span className="text-yellow-300 text-xs">
                    Reintentando... ({retryCount}/2)
                  </span>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
      
      {/* Formulario */}
      <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
        {/* Grid de inputs - Responsive */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
          {/* Nombre */}
          <div className="space-y-1 sm:space-y-2">
            <label htmlFor="name" className="block text-xs sm:text-sm font-medium text-white">
              Nombre <span className="text-[#4ADE80]">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              maxLength={100}
              className={`w-full px-3 py-2 sm:py-2.5 text-sm bg-white/10 backdrop-blur-sm border rounded-lg focus:ring-2 transition-all duration-300 placeholder-white/40 text-white ${
                errors.name && touched.name
                  ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500/20'
                  : 'border-white/20 focus:border-[#4ADE80] focus:ring-[#4ADE80]/20'
              }`}
              placeholder="Tu nombre completo"
            />
            {errors.name && touched.name && (
              <p className="text-red-300 text-xs mt-1 flex items-center gap-1">
                <ExclamationCircleIcon className="h-3 w-3" />
                {errors.name}
              </p>
            )}
          </div>
          
          {/* Email */}
          <div className="space-y-1 sm:space-y-2">
            <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-white">
              Email <span className="text-[#4ADE80]">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              maxLength={255}
              className={`w-full px-3 py-2 sm:py-2.5 text-sm bg-white/10 backdrop-blur-sm border rounded-lg focus:ring-2 transition-all duration-300 placeholder-white/40 text-white ${
                errors.email && touched.email
                  ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500/20'
                  : 'border-white/20 focus:border-[#4ADE80] focus:ring-[#4ADE80]/20'
              }`}
              placeholder="ejemplo@empresa.com"
            />
            {errors.email && touched.email && (
              <p className="text-red-300 text-xs mt-1 flex items-center gap-1">
                <ExclamationCircleIcon className="h-3 w-3" />
                {errors.email}
              </p>
            )}
          </div>
          
          {/* Teléfono */}
          <div className="space-y-1 sm:space-y-2">
            <label htmlFor="phone" className="block text-xs sm:text-sm font-medium text-white">
              Teléfono
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={30}
              className={`w-full px-3 py-2 sm:py-2.5 text-sm bg-white/10 backdrop-blur-sm border rounded-lg focus:ring-2 transition-all duration-300 placeholder-white/40 text-white ${
                errors.phone && touched.phone
                  ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500/20'
                  : 'border-white/20 focus:border-[#4ADE80] focus:ring-[#4ADE80]/20'
              }`}
              placeholder="+54 351 123-4567"
            />
            {errors.phone && touched.phone && (
              <p className="text-red-300 text-xs mt-1 flex items-center gap-1">
                <ExclamationCircleIcon className="h-3 w-3" />
                {errors.phone}
              </p>
            )}
          </div>
          
          {/* Empresa */}
          <div className="space-y-1 sm:space-y-2">
            <label htmlFor="company" className="block text-xs sm:text-sm font-medium text-white">
              Empresa
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={100}
              className="w-full px-3 py-2 sm:py-2.5 text-sm bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg focus:ring-2 focus:ring-[#4ADE80]/20 focus:border-[#4ADE80] transition-all duration-300 placeholder-white/40 text-white"
              placeholder="Nombre de empresa (opcional)"
            />
          </div>
        </div>
        
        {/* Mensaje */}
        <div className="space-y-1 sm:space-y-2">
          <div className="flex justify-between items-center">
            <label htmlFor="message" className="block text-xs sm:text-sm font-medium text-white">
              Mensaje <span className="text-[#4ADE80]">*</span>
            </label>
            <span className="text-white/40 text-xs">
              {formData.message.length}/2000
            </span>
          </div>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            maxLength={2000}
            rows={4}
            className={`w-full px-3 py-2 sm:py-2.5 text-sm bg-white/10 backdrop-blur-sm border rounded-lg focus:ring-2 transition-all duration-300 placeholder-white/40 text-white resize-none ${
              errors.message && touched.message
                ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500/20'
                : 'border-white/20 focus:border-[#4ADE80] focus:ring-[#4ADE80]/20'
            }`}
            placeholder="Cuéntanos sobre tu proyecto, consulta o requerimiento..."
          />
          {errors.message && touched.message && (
            <p className="text-red-300 text-xs mt-1 flex items-center gap-1">
              <ExclamationCircleIcon className="h-3 w-3" />
              {errors.message}
            </p>
          )}
        </div>
        
        {/* Botones y contacto */}
        <div className="pt-2 sm:pt-4">
          {/* Botones - Responsive */}
          <div className="flex flex-col-reverse sm:flex-row gap-2 mb-3 sm:mb-4">
            {/* Botón Cancelar (móvil: arriba, desktop: derecha) */}
            {onClose && (
              <motion.button
                type="button"
                onClick={onClose}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full sm:w-auto px-4 py-2.5 bg-white/10 backdrop-blur-sm text-white text-sm font-medium rounded-lg hover:bg-white/20 transition-all duration-300 border border-white/30 order-first sm:order-none"
              >
                Cancelar
              </motion.button>
            )}
            
            {/* Botón Enviar */}
            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] text-white text-sm font-medium rounded-lg hover:shadow-lg transition-all duration-300 border border-[#4ADE80]/50 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Enviando...</span>
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="h-4 w-4" />
                  <span>Enviar Mensaje</span>
                </>
              )}
            </motion.button>
          </div>
          
          {/* Información de contacto - Responsive */}
          <div className="border-t border-white/20 pt-3 sm:pt-4">
            <div className="flex flex-col items-center gap-3 text-center">
              <span className="text-white/70 text-xs sm:text-sm">Contacto directo:</span>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 w-full">
                {/* Email */}
                <a 
                  href="mailto:contacto@algorithmia.com.ar" 
                  className="text-white hover:text-[#4ADE80] text-xs sm:text-sm transition-colors flex items-center gap-2 group"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <EnvelopeIcon className="h-4 w-4 text-[#4ADE80] group-hover:scale-110 transition-transform" />
                  <span className="break-all">contacto@algorithmia.com.ar</span>
                </a>
                
                {/* Teléfonos */}
                <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
                  <div className="flex items-center gap-2 text-white text-xs sm:text-sm">
                    <PhoneIcon className="h-4 w-4 text-[#4ADE80] flex-shrink-0" />
                    <span>+54 (351) 2489015</span>
                  </div>
                  <div className="flex items-center gap-2 text-white text-xs sm:text-sm">
                    <PhoneIcon className="h-4 w-4 text-[#4ADE80] flex-shrink-0" />
                    <span>+54 (351) 2021393</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </motion.div>
  );
};

export default ContactForm;